/**
 * PriceDisplay — money in tabular figures with a gold currency mark.
 *
 * `amount` is a **display (major-unit) number** — callers must convert from the
 * codebase's minor-unit int64 (CLAUDE.md money rule) before passing it here.
 * e.g. int64 value 9900 (EUR cents) → amount={99}
 *
 * Optional per-night suffix and a struck-through original price (sale).
 * Currency symbol by ISO 4217; multi-char codes (RSD, etc.) render as a code
 * prefix rather than a symbol.
 *
 * Re-authored from ../design-system/components/data-display/PriceDisplay.jsx
 * onto Tailwind v4 / cn() / no injected styles.
 */
import { type HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import { currencySymbol, groupAmount } from '@/lib/money';

export interface PriceDisplayProps extends HTMLAttributes<HTMLSpanElement> {
  /** Display amount in major units (caller converts from minor-unit int64). */
  amount: number;
  /** ISO 4217 currency code. Default 'EUR'. */
  currency?: string;
  /** Optional "per X" suffix, e.g. 'night'. */
  per?: string;
  /** Optional struck-through original amount (major units). */
  was?: number;
  size?: 'sm' | 'md' | 'lg';
}

// Amount font size per size prop (DS data-size values)
const AMOUNT_SIZE: Record<NonNullable<PriceDisplayProps['size']>, string> = {
  sm: 'text-[var(--text-md)]',
  md: 'text-[var(--text-xl)]',
  lg: 'text-[var(--text-3xl)]',
};

// Currency mark font size per size prop
const CUR_SIZE: Record<NonNullable<PriceDisplayProps['size']>, string> = {
  sm: 'text-[var(--text-sm)]',
  md: 'text-[var(--text-md)]',
  lg: 'text-[var(--text-xl)]',
};

// Per/was font size per size prop
const SUB_SIZE: Record<NonNullable<PriceDisplayProps['size']>, string> = {
  sm: 'text-[var(--text-xs)]',
  md: 'text-[var(--text-sm)]',
  lg: 'text-[var(--text-base)]',
};

export function PriceDisplay({
  amount,
  currency = 'EUR',
  per,
  was,
  size = 'md',
  className,
  ...rest
}: PriceDisplayProps) {
  const sym = currencySymbol(currency);
  const isCode = sym.length > 1;

  return (
    <span
      className={cn(
        'inline-flex items-baseline gap-1.5 font-mono tabular-nums text-[var(--text-heading)]',
        className,
      )}
      {...rest}
    >
      {/* Currency symbol / code */}
      <span
        className={cn(
          'font-semibold text-[var(--ollio-gold-deep)]',
          CUR_SIZE[size],
        )}
      >
        {sym}
      </span>

      {/* Main amount */}
      <span className={cn('font-semibold', AMOUNT_SIZE[size])}>
        {groupAmount(amount)}
      </span>

      {/* Struck-through original price (DS order: was before per) */}
      {was != null && (
        <span
          data-was
          className={cn(
            'font-sans text-[var(--text-muted)] line-through',
            SUB_SIZE[size],
          )}
        >
          {sym}{isCode ? ' ' : ''}{groupAmount(was)}
        </span>
      )}

      {/* Per suffix */}
      {per !== undefined && (
        <span className={cn('font-sans text-[var(--text-muted)]', SUB_SIZE[size])}>
          / {per}
        </span>
      )}
    </span>
  );
}
